import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationExtras } from '@angular/router';
import { ModalController } from '@ionic/angular/standalone';
import { SettingsPage } from '../../trainer/settings/settings.page';
import { sideSheetEnter, sideSheetLeave } from '../../utils/side-sheet.animation';
import { IonIcon, IonTabBar, IonTabButton, IonLabel, IonAvatar } from '@ionic/angular/standalone';
import { NavTab, TransitionDirection } from '../../models/nav-tab.interface';
import { TransitionService } from '../../services/transition.service';
import { ChatService } from '../../services/chat.service';
import { Subscription } from 'rxjs';
import * as allIcons from 'ionicons/icons';
import { addIcons } from 'ionicons';
addIcons(allIcons);

@Component({
  selector: 'app-bottom-nav',
  standalone: true,
  imports: [IonIcon, IonTabBar, IonTabButton, IonLabel, IonAvatar],
  templateUrl: './bottom-nav.component.html',
  styleUrls: ['./bottom-nav.component.scss']
})
export class BottomNavComponent implements OnInit, OnDestroy {
  @Input() tabs: NavTab[] = [];
  @Input() activeTabId: string = '';
  /** URL da foto do perfil do usuário (exibida na tab Perfil para alunos) */
  @Input() profileImageUrl: string | null = null;
  @Output() tabSelected = new EventEmitter<NavTab>();

  /** Contador de conversas não lidas (para indicador na tab Chat) */
  unreadChatCount = 0;
  private unreadSubscription?: Subscription;

  constructor(
    private router: Router,
    private transitionService: TransitionService,
    private chatService: ChatService,
    private modalCtrl: ModalController
  ) {}

  ngOnInit(): void {
    this.unreadSubscription = this.chatService.unreadCount$.subscribe(
      count => (this.unreadChatCount = count)
    );
    // Garante que os chats sejam carregados para atualizar o contador de não lidas
    this.chatService.loadChats();
  }

  ngOnDestroy(): void {
    this.unreadSubscription?.unsubscribe();
  }

  /** Verifica se a tab é a de chat (student-chats ou trainer-chats) */
  isChatTab(tab: NavTab): boolean {
    return tab.id === 'student-chats' || tab.id === 'trainer-chats';
  }

  onTabClick(tab: NavTab, event?: Event): void {
    if (event) {
      event.preventDefault();
    }

    // Ajustes do personal abre num painel lateral, sem trocar de página
    if (tab.id === 'trainer-settings') {
      void this.openSettingsSheet();
      return;
    }

    this.tabSelected.emit(tab);

    const currentTabIndex = this.tabs.findIndex(t => t.id === this.activeTabId);
    const targetTabIndex = this.tabs.findIndex(t => t.id === tab.id);

    // Determina a direção da transição
    let direction: TransitionDirection = tab.transitionDirection || 'forward';
    
    // Se não especificado, determina automaticamente baseado na posição das tabs
    if (!tab.transitionDirection && currentTabIndex >= 0 && targetTabIndex >= 0) {
      direction = targetTabIndex > currentTabIndex ? 'forward' : 'back';
    }

    // Define a direção da transição ANTES de navegar
    this.transitionService.setNextTransitionDirection(direction);

    // Navega com a direção da transição no state (para backup)
    const extras: NavigationExtras = {
      state: {
        transitionDirection: direction
      },
      queryParamsHandling: 'merge'
    };

    this.router.navigate([tab.route], extras);
  }

  /** Abre os ajustes do personal num painel lateral. */
  async openSettingsSheet(): Promise<void> {
    const modal = await this.modalCtrl.create({
      component: SettingsPage,
      componentProps: { inSheet: true },
      cssClass: 'settings-side-sheet',
      enterAnimation: sideSheetEnter,
      leaveAnimation: sideSheetLeave,
    });
    await modal.present();
  }
}
